import { useParams } from 'react-router';
import { useMemo } from 'react';
import { useFetchSigningDetails } from 'receivable-invoices-js/react/api/queries/fetch-signing-details/fetch-signing-details';
import { useFetchClientPortalSettings } from 'receivable-invoices-js/react/api/queries/fetch-client-portal-settings';
import { QUOTE_STATUSES } from 'receivable-invoices-js/react/constants/quotes';

interface UseRouteQueryReturn {
  isLoading: boolean;
  isError: boolean;
  isNotAvailable: boolean;
  signatureLink?: string;
  status?: string;
  error?: string;
  isClientPortalEnabled?: boolean;
  clientId: string;
  quoteId: string;
}

export function useRouteQuery(): UseRouteQueryReturn {
  const params = useParams<{ id: string; clientId: string }>();
  const quoteId = params.id || '';
  const clientId = params.clientId || '';

  const {
    data: signingDetails,
    isLoading: signingDetailsLoading,
    isError: signingDetailsError,
  } = useFetchSigningDetails(
    { quoteId, clientId },
    {},
    {
      enabled: Boolean(quoteId && clientId),
    }
  );

  const {
    data: clientPortalSettings,
    isLoading: clientPortalSettingsLoading,
    isError: clientPortalSettingsError,
  } = useFetchClientPortalSettings({ clientId }, {}, { enabled: Boolean(clientId) });

  const signatureLink = signingDetails?.electronicSignatureUrl;
  const status = signingDetails?.status;
  const error = signingDetails?.error;
  const isClientPortalEnabled = clientPortalSettings?.clientPortalEnabled ?? false;
  const isLoading = signingDetailsLoading || clientPortalSettingsLoading;

  const { isError, isNotAvailable } = useMemo(() => {
    // Check for errors first, even if still loading
    if (signingDetailsError || clientPortalSettingsError || error) {
      return { isError: true, isNotAvailable: false };
    }

    if (isLoading || !signingDetails) {
      return { isError: false, isNotAvailable: false };
    }

    if (!signatureLink) {
      return { isError: true, isNotAvailable: false };
    }

    if (status === QUOTE_STATUSES.CANCELED) {
      return { isError: false, isNotAvailable: true };
    }

    return { isError: false, isNotAvailable: false };
  }, [
    isLoading,
    signingDetails,
    signingDetailsError,
    clientPortalSettingsError,
    signatureLink,
    error,
    status,
  ]);

  return {
    isLoading,
    isError,
    isNotAvailable,
    signatureLink,
    status,
    error,
    isClientPortalEnabled,
    clientId,
    quoteId,
  };
}
